// eslint-disable-next-line import/no-cycle, restrict-imports/restrict-imports
import type { SingleProcessEventNamesAndArguments } from '../foreground/types/events';
// eslint-disable-next-line import/no-cycle
import { portalGate as foregroundPortalGate } from './portalGates/toForeground';

export type ManifestEntryWithOptionalId = { id?: string | null; };

export type ManifestLoadingTrackerOptions<TManifestEntry, TLoadedItem> = {
  eventName: keyof SingleProcessEventNamesAndArguments;
  itemName: string;
  getEntryId?: (entry: TManifestEntry) => string | undefined | null;
  isItemLoaded?: (item: TLoadedItem) => boolean;
};

export type ManifestLoadingTracker<TManifestEntry, TLoadedItem> = {
  handleItemsLoaded: (itemMap: { [id: string]: TLoadedItem; }) => void;
  listenForEntries: (docId: string, entries: TManifestEntry[]) => void;
};

function defaultGetEntryId(entry: ManifestEntryWithOptionalId): string | undefined | null {
  return entry.id;
}

function defaultIsItemLoaded<TLoadedItem>(item: TLoadedItem): boolean {
  return Boolean(item);
}

// Creates a tracker that monitors loading progress of manifest items and emits events with dynamically named fields.
// The itemName parameter determines the field names in emitted events:
// e.g., itemName "chunk" -> event fields "loadedChunkCount" and "totalChunkCount"
export function createManifestLoadingTracker<TManifestEntry extends ManifestEntryWithOptionalId, TLoadedItem>(
  options: ManifestLoadingTrackerOptions<TManifestEntry, TLoadedItem>,
): ManifestLoadingTracker<TManifestEntry, TLoadedItem> {
  const {
    eventName,
    itemName,
    getEntryId = defaultGetEntryId as (entry: TManifestEntry) => string | undefined | null,
    isItemLoaded = defaultIsItemLoaded,
  } = options;

  const capitalizedItemName = itemName.charAt(0).toUpperCase() + itemName.slice(1);
  const loadedCountField = `loaded${capitalizedItemName}Count`;
  const totalCountField = `total${capitalizedItemName}Count`;

  const entryByItemId: { [id: string]: { docId: string; isLoaded?: boolean; }; } = {};
  const itemIdsByDocId: { [docId: string]: string[]; } = {};

  function listenForEntries(docId: string, entries: TManifestEntry[]): void {
    const ids = entries
      .map(getEntryId)
      .filter((id): id is string => typeof id === 'string' && id.length > 0);
    for (const id of ids) {
      entryByItemId[id] = { docId };
    }
    itemIdsByDocId[docId] = ids;
  }

  function handleItemsLoaded(itemMap: { [id: string]: TLoadedItem; }): void {
    const docsToUpdate = new Set<string>();
    for (const itemId of Object.keys(itemMap)) {
      const entry = entryByItemId[itemId];
      if (!entry) {
        continue;
      }
      if (!isItemLoaded(itemMap[itemId])) {
        continue;
      }
      entryByItemId[itemId].isLoaded = true;
      docsToUpdate.add(entry.docId);
    }

    docsToUpdate.forEach((docId) => {
      const itemIds = itemIdsByDocId[docId];
      if (!itemIds || itemIds.length === 0) {
        return;
      }
      let loadedCount = 0;
      for (const id of itemIds) {
        if (entryByItemId[id]?.isLoaded) {
          loadedCount += 1;
        }
      }
      const totalCount = itemIds.length;
      foregroundPortalGate.emit(eventName, {
        docId,
        [loadedCountField]: loadedCount,
        [totalCountField]: totalCount,
      } as SingleProcessEventNamesAndArguments[typeof eventName]);
      if (loadedCount === totalCount) {
        for (const id of itemIds) {
          delete entryByItemId[id];
        }
        delete itemIdsByDocId[docId];
      }
    });
  }

  return {
    handleItemsLoaded,
    listenForEntries,
  };
}
